import React, { useState, useRef } from 'react';

// Roster search + filter row. Lives in its own component so the dashboard can
// render it OUTSIDE the roster card (as a pinned full-bleed bar — the card's
// `overflow: hidden` would clip a sticky/full-bleed child) while the secondary
// roster surfaces keep it inline inside the table.
//
//   value / onChange — search text (always controlled by the caller)
//   countLabel       — optional "N results" hint shown while searching
//   extras           — slot after the search box (the dashboard filter chips)
//   trailing         — far-right slot (the board's clinician scope note; the role switcher, once pinned)
export default function RosterToolbar({
  showSearch = false,
  value = '',
  onChange,
  countLabel = null,
  extras = null,
  trailing = null,
  className = '',
}) {
  const [open, setOpen] = useState(!!value);
  const inputRef = useRef(null);

  const close = () => { onChange?.(''); setOpen(false); };

  return (
    <div className={`roster-toolbar${className ? ` ${className}` : ''}`}>
      {showSearch && (!open ? (
        <button
          type="button"
          onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 60); }}
          aria-label="Search patients"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            height: 38, padding: '0 14px', borderRadius: 10,
            border: '1px solid var(--wi-divider)',
            background: 'var(--wi-background-primary)',
            cursor: 'pointer', transition: 'box-shadow 200ms',
            color: 'var(--wi-text-secondary)', fontSize: 13, fontFamily: 'var(--wi-font-family)',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
            <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth="2" fill="none" />
            <line x1="11.5" y1="11.5" x2="16" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>Search patients…</span>
        </button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, maxWidth: 420 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <svg width="16" height="16" viewBox="0 0 18 18" fill="none" style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              pointerEvents: 'none', color: 'var(--wi-text-secondary)',
            }}>
              <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth="2" fill="none" />
              <line x1="11.5" y1="11.5" x2="16" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={e => onChange?.(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') close(); }}
              placeholder="Name, condition, risk, EHR ID…"
              className="call-notes-input"
              style={{ paddingLeft: 36, paddingTop: 8, paddingBottom: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          {value && countLabel && (
            <span className="detail1 text-secondary" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              {countLabel}
            </span>
          )}
          <button
            type="button"
            onClick={close}
            aria-label="Close search"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 28, height: 28, borderRadius: 6, border: 'none',
              background: 'var(--wi-background-primary)', cursor: 'pointer', flexShrink: 0,
              color: 'var(--wi-text-secondary)', fontSize: 16, fontWeight: 500, lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
      ))}
      {extras}
      {trailing && <div className="roster-toolbar__trailing">{trailing}</div>}
    </div>
  );
}
